import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { 
  Sparkles, ArrowRight, ArrowLeft, Check, Camera, User,
  Music, Film, Gamepad2, BookOpen, Plane, Utensils, 
  Dumbbell, Palette, Code, Briefcase, Heart, Laugh,
  Globe, TrendingUp, Mic, ShoppingBag, Car, Home
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Interest categories with icons
const INTEREST_CATEGORIES = [
  { id: 'music', label: 'Music', icon: Music, color: 'bg-pink-500' },
  { id: 'movies', label: 'Movies & TV', icon: Film, color: 'bg-purple-500' },
  { id: 'gaming', label: 'Gaming', icon: Gamepad2, color: 'bg-green-500' },
  { id: 'books', label: 'Books', icon: BookOpen, color: 'bg-amber-500' },
  { id: 'travel', label: 'Travel', icon: Plane, color: 'bg-blue-500' },
  { id: 'food', label: 'Food & Cooking', icon: Utensils, color: 'bg-orange-500' },
  { id: 'fitness', label: 'Fitness', icon: Dumbbell, color: 'bg-red-500' },
  { id: 'art', label: 'Art & Design', icon: Palette, color: 'bg-indigo-500' },
  { id: 'tech', label: 'Technology', icon: Code, color: 'bg-cyan-500' },
  { id: 'business', label: 'Business', icon: Briefcase, color: 'bg-slate-500' },
  { id: 'lifestyle', label: 'Lifestyle', icon: Heart, color: 'bg-rose-500' },
  { id: 'comedy', label: 'Comedy', icon: Laugh, color: 'bg-yellow-500' },
  { id: 'news', label: 'News & Politics', icon: Globe, color: 'bg-gray-500' },
  { id: 'sports', label: 'Sports', icon: TrendingUp, color: 'bg-emerald-500' },
  { id: 'podcasts', label: 'Podcasts', icon: Mic, color: 'bg-violet-500' },
  { id: 'fashion', label: 'Fashion', icon: ShoppingBag, color: 'bg-fuchsia-500' },
  { id: 'cars', label: 'Cars & Motors', icon: Car, color: 'bg-zinc-500' },
  { id: 'home', label: 'Home & Garden', icon: Home, color: 'bg-lime-500' },
];

const Onboarding = ({ user, setUser }) => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [suggestedUsers, setSuggestedUsers] = useState([]);
  const [followedUsers, setFollowedUsers] = useState([]);
  
  // Profile data
  const [profile, setProfile] = useState({
    avatar: user?.avatar || '',
    bio: user?.bio || '',
    username: user?.username || '',
  });
  
  // Selected interests
  const [selectedInterests, setSelectedInterests] = useState([]);

  useEffect(() => {
    // Fetch suggested users for step 3
    if (step === 3) {
      fetchSuggestedUsers();
    }
  }, [step]);

  const fetchSuggestedUsers = async () => {
    try {
      const response = await axios.get(`${API_URL}/users/suggestions`, { withCredentials: true });
      setSuggestedUsers(response.data.slice(0, 12));
    } catch (error) {
      console.error('Failed to fetch suggestions');
    }
  };

  const handleAvatarUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      // For now, create a preview URL (in production, upload to server)
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfile({ ...profile, avatar: reader.result });
      };
      reader.readAsDataURL(file);
    }
  };

  const toggleInterest = (interestId) => {
    if (selectedInterests.includes(interestId)) {
      setSelectedInterests(selectedInterests.filter(id => id !== interestId));
    } else {
      setSelectedInterests([...selectedInterests, interestId]);
    }
  };

  const toggleFollow = async (userId) => {
    try {
      if (followedUsers.includes(userId)) {
        await axios.delete(`${API_URL}/users/follow/${userId}`, { withCredentials: true });
        setFollowedUsers(followedUsers.filter(id => id !== userId));
      } else {
        await axios.post(`${API_URL}/users/follow/${userId}`, {}, { withCredentials: true });
        setFollowedUsers([...followedUsers, userId]);
      }
    } catch (error) {
      toast.error('Failed to update follow status');
    }
  };

  const saveProfile = async () => {
    setLoading(true);
    try {
      // Save profile data
      await axios.put(`${API_URL}/users/profile`, {
        bio: profile.bio,
        avatar: profile.avatar,
      }, { withCredentials: true });
      
      // Save interests
      await axios.post(`${API_URL}/users/interests`, {
        interests: selectedInterests
      }, { withCredentials: true });
      
      toast.success('Profile setup complete!');
      
      // Update user context
      if (setUser) {
        setUser(prev => ({
          ...prev,
          bio: profile.bio,
          avatar: profile.avatar,
          interests: selectedInterests,
          onboarding_complete: true
        }));
      }
      
      // Navigate to feed
      navigate('/feed');
    } catch (error) {
      toast.error('Failed to save profile');
    } finally {
      setLoading(false);
    }
  };

  const skipOnboarding = () => {
    navigate('/feed');
  };

  const totalSteps = 3;

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-accent-50 to-secondary-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 flex items-center justify-center p-6">
      <div className="w-full max-w-2xl">
        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-semibold text-gray-600 dark:text-gray-400">
              Step {step} of {totalSteps}
            </span>
            <button
              onClick={skipOnboarding}
              className="text-sm text-gray-500 hover:text-primary transition-colors"
              data-testid="skip-onboarding-btn"
            >
              Skip for now
            </button>
          </div>
          <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
            <motion.div
              className="h-full bg-gradient-to-r from-primary to-accent"
              initial={{ width: 0 }}
              animate={{ width: `${(step / totalSteps) * 100}%` }}
              transition={{ duration: 0.3 }}
            />
          </div>
        </div>

        <AnimatePresence mode="wait">
          {/* Step 1: Profile Setup */}
          {step === 1 && (
            <motion.div
              key="step1"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="bg-white/70 dark:bg-gray-800/70 backdrop-blur-md rounded-3xl p-8 shadow-2xl border border-white/20"
            >
              <div className="text-center mb-8">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-primary to-accent flex items-center justify-center mx-auto mb-4">
                  <User className="w-8 h-8 text-white" />
                </div>
                <h2 className="text-2xl font-heading font-bold text-gray-900 dark:text-white mb-2">
                  Set Up Your Profile
                </h2>
                <p className="text-gray-600 dark:text-gray-400">
                  Let people know who you are
                </p>
              </div>

              <div className="space-y-6">
                {/* Avatar Upload */}
                <div className="flex flex-col items-center">
                  <div className="relative">
                    <Avatar className="w-28 h-28 border-4 border-white shadow-xl">
                      <AvatarImage src={profile.avatar} />
                      <AvatarFallback className="bg-gradient-to-br from-primary to-accent text-white text-3xl font-bold">
                        {user?.name?.charAt(0) || 'U'}
                      </AvatarFallback>
                    </Avatar>
                    <label className="absolute bottom-0 right-0 w-10 h-10 bg-primary rounded-full flex items-center justify-center cursor-pointer shadow-lg hover:bg-primary-700 transition-colors">
                      <Camera className="w-5 h-5 text-white" />
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleAvatarUpload}
                        className="sr-only"
                        data-testid="avatar-upload-input"
                      />
                    </label>
                  </div>
                  <p className="text-sm text-gray-500 mt-2">Click to upload photo</p>
                </div>

                {/* Username Display */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    Your Handle
                  </label>
                  <div className="flex items-center gap-2 p-3 bg-gray-100 dark:bg-gray-700 rounded-xl">
                    <span className="text-primary font-bold">@{profile.username || user?.username}</span>
                    <Check className="w-4 h-4 text-green-500" />
                  </div>
                </div>

                {/* Bio */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    Bio
                  </label>
                  <Textarea
                    value={profile.bio}
                    onChange={(e) => setProfile({ ...profile, bio: e.target.value })}
                    placeholder="Tell the world a little about yourself..."
                    className="rounded-xl border-2 resize-none"
                    rows={3}
                    maxLength={160}
                    data-testid="bio-input"
                  />
                  <p className="text-xs text-gray-500 mt-1 text-right">
                    {profile.bio.length}/160
                  </p>
                </div>
              </div>

              <div className="flex justify-end mt-8">
                <Button
                  onClick={() => setStep(2)}
                  className="rounded-full bg-primary hover:bg-primary-700 text-white font-bold px-8 h-12"
                  data-testid="next-step-btn"
                >
                  Continue
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </motion.div>
          )}

          {/* Step 2: Select Interests */}
          {step === 2 && (
            <motion.div
              key="step2"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="bg-white/70 dark:bg-gray-800/70 backdrop-blur-md rounded-3xl p-8 shadow-2xl border border-white/20"
            >
              <div className="text-center mb-8">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-accent to-secondary flex items-center justify-center mx-auto mb-4">
                  <Sparkles className="w-8 h-8 text-white" />
                </div>
                <h2 className="text-2xl font-heading font-bold text-gray-900 dark:text-white mb-2">
                  What Are You Into?
                </h2>
                <p className="text-gray-600 dark:text-gray-400">
                  Select at least 3 interests to personalize your feed
                </p>
              </div>

              <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 mb-8">
                {INTEREST_CATEGORIES.map((interest) => {
                  const Icon = interest.icon;
                  const isSelected = selectedInterests.includes(interest.id);
                  return (
                    <button
                      key={interest.id}
                      onClick={() => toggleInterest(interest.id)}
                      className={`p-4 rounded-2xl border-2 transition-all flex flex-col items-center gap-2 ${
                        isSelected
                          ? `${interest.color} border-transparent text-white shadow-lg scale-105`
                          : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                      }`}
                      data-testid={`interest-${interest.id}`}
                    >
                      <Icon className={`w-6 h-6 ${isSelected ? 'text-white' : 'text-gray-600 dark:text-gray-400'}`} />
                      <span className={`text-xs font-semibold ${isSelected ? 'text-white' : 'text-gray-700 dark:text-gray-300'}`}>
                        {interest.label}
                      </span>
                      {isSelected && (
                        <Check className="w-4 h-4 absolute top-2 right-2" />
                      )}
                    </button>
                  );
                })}
              </div>

              <p className="text-center text-sm text-gray-500 mb-6">
                {selectedInterests.length} selected {selectedInterests.length < 3 && `(${3 - selectedInterests.length} more needed)`}
              </p>

              <div className="flex justify-between">
                <Button
                  onClick={() => setStep(1)}
                  variant="outline"
                  className="rounded-full font-bold px-6 h-12"
                  data-testid="prev-step-btn"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
                <Button
                  onClick={() => setStep(3)}
                  disabled={selectedInterests.length < 3}
                  className="rounded-full bg-primary hover:bg-primary-700 text-white font-bold px-8 h-12 disabled:opacity-50"
                  data-testid="next-step-btn"
                >
                  Continue
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </motion.div>
          )}

          {/* Step 3: Follow Suggestions */}
          {step === 3 && (
            <motion.div
              key="step3"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="bg-white/70 dark:bg-gray-800/70 backdrop-blur-md rounded-3xl p-8 shadow-2xl border border-white/20"
            >
              <div className="text-center mb-8">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-secondary to-primary flex items-center justify-center mx-auto mb-4">
                  <Heart className="w-8 h-8 text-white" />
                </div>
                <h2 className="text-2xl font-heading font-bold text-gray-900 dark:text-white mb-2">
                  Follow Some People
                </h2>
                <p className="text-gray-600 dark:text-gray-400">
                  Get started by following accounts you might like
                </p>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-8 max-h-[400px] overflow-y-auto">
                {suggestedUsers.map((suggestedUser) => {
                  const isFollowing = followedUsers.includes(suggestedUser.user_id);
                  return (
                    <div
                      key={suggestedUser.user_id}
                      className="p-4 bg-gray-50 dark:bg-gray-700 rounded-2xl text-center"
                    >
                      <Avatar className="w-16 h-16 mx-auto mb-3">
                        <AvatarImage src={suggestedUser.avatar} />
                        <AvatarFallback className="bg-gradient-to-br from-primary to-accent text-white font-bold">
                          {suggestedUser.name?.charAt(0)}
                        </AvatarFallback>
                      </Avatar>
                      <p className="font-semibold text-gray-900 dark:text-white text-sm truncate">
                        {suggestedUser.name}
                      </p>
                      <p className="text-xs text-gray-500 mb-3 truncate">
                        @{suggestedUser.username}
                      </p>
                      <Button
                        onClick={() => toggleFollow(suggestedUser.user_id)}
                        size="sm"
                        className={`w-full rounded-full text-xs ${
                          isFollowing
                            ? 'bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300'
                            : 'bg-primary hover:bg-primary-700 text-white'
                        }`}
                        data-testid={`follow-${suggestedUser.user_id}`}
                      >
                        {isFollowing ? 'Following' : 'Follow'}
                      </Button>
                    </div>
                  );
                })}
              </div>

              {suggestedUsers.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <p>No suggestions available yet.</p>
                  <p className="text-sm">You can find people to follow later!</p>
                </div>
              )}

              <div className="flex justify-between">
                <Button
                  onClick={() => setStep(2)}
                  variant="outline"
                  className="rounded-full font-bold px-6 h-12"
                  data-testid="prev-step-btn"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
                <Button
                  onClick={saveProfile}
                  disabled={loading}
                  className="rounded-full bg-gradient-to-r from-primary to-accent hover:opacity-90 text-white font-bold px-8 h-12"
                  data-testid="finish-onboarding-btn"
                >
                  {loading ? 'Setting up...' : "Let's Go!"}
                  <Sparkles className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default Onboarding;
